import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Layers, Plus, Edit3, Trash2, Save, Search, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import InfoTooltip from "@/components/admin/InfoTooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const titleCase = (s) =>
  (s || "")
    .split(/[\s_-]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

const FIELD_DEFS = [
  { key: "name", label: "Service name" },
  { key: "category_id", label: "Category", type: "category-select" },
  { key: "duration_min", label: "Duration (min)", type: "number" },
  { key: "price_low", label: "Price low ($)", type: "number" },
  { key: "price_high", label: "Price high ($)", type: "number" },
  { key: "is_active", label: "Active", type: "bool" },
];

/**
 * AdminServiceCatalogPage - Iter 9.31
 *
 * Replaces the legacy CategoryManagementSection mount at /admin/product/service-catalog.
 * - Loads canonical service_catalog rows grouped by category_id
 * - Category column is a relational <Select> bound to the live `categories` collection
 * - Collapsible groups per category
 * - CRUD: create, edit, delete with optimistic refresh
 */
export default function AdminServiceCatalogPage() {
  const [grouped, setGrouped] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // service_catalog_id
  const [draft, setDraft] = useState({});
  const [collapsed, setCollapsed] = useState({}); // {category_id: true}
  const [newCategory, setNewCategory] = useState("");
  const [newDraft, setNewDraft] = useState({});

  const auth = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` }),
    []
  );

  const loadCategories = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/category-master`, { headers: auth() });
      if (!r.ok) return;
      const d = await r.json();
      const rows = (d.masters || []).map((m) => ({
        category_id: m.category_id,
        label: titleCase(m.label || m.category_id),
      }));
      setCategories(rows);
    } catch (e) {
      // silent
    }
  }, [auth]);

  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/service-catalog`, { headers: auth() });
      if (r.ok) {
        const d = await r.json();
        setGrouped(d.services || {});
      } else {
        toast.error("Could not load service catalog");
      }
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    loadCategories();
    loadServices();
  }, [loadCategories, loadServices]);

  const startEdit = (svc) => {
    setEditing(svc.service_catalog_id);
    setDraft({ ...svc });
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft({});
  };

  const saveEdit = async () => {
    try {
      const body = { ...draft };
      ["duration_min", "price_low", "price_high"].forEach((k) => {
        if (body[k] === "" || body[k] === null) body[k] = null;
        else if (body[k] !== undefined) body[k] = Number(body[k]);
      });
      const r = await fetch(`${API}/admin/service-catalog/${editing}`, {
        method: "PUT",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success("Saved");
        cancelEdit();
        loadServices();
      } else {
        toast.error("Save failed");
      }
    } catch {
      toast.error("Save failed");
    }
  };

  const deleteService = async (sid) => {
    if (!window.confirm("Delete this service?")) return;
    const r = await fetch(`${API}/admin/service-catalog/${sid}`, {
      method: "DELETE",
      headers: auth(),
    });
    if (r.ok) {
      toast.success("Deleted");
      loadServices();
    } else {
      toast.error("Delete failed");
    }
  };

  const startNew = (categoryId) => {
    setNewCategory(categoryId);
    setNewDraft({ category_id: categoryId, is_active: true });
  };

  const createNew = async () => {
    if (!newDraft.name?.trim()) {
      toast.error("Name required");
      return;
    }
    const body = { ...newDraft };
    ["duration_min", "price_low", "price_high"].forEach((k) => {
      if (body[k] === "" || body[k] === null || body[k] === undefined) delete body[k];
      else body[k] = Number(body[k]);
    });
    const r = await fetch(`${API}/admin/service-catalog`, {
      method: "POST",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (r.ok) {
      toast.success("Created");
      setNewCategory("");
      setNewDraft({});
      loadServices();
    } else {
      toast.error("Create failed");
    }
  };

  const filteredGroups = useMemo(() => {
    const out = {};
    const term = search.trim().toLowerCase();
    Object.entries(grouped).forEach(([cid, rows]) => {
      const filtered = !term
        ? rows
        : rows.filter(
            (r) =>
              (r.name || "").toLowerCase().includes(term) ||
              (r.slug || "").toLowerCase().includes(term)
          );
      if (filtered.length > 0 || !term) out[cid] = filtered;
    });
    return out;
  }, [grouped, search]);

  const allCategoryIds = useMemo(() => {
    const ids = new Set([
      ...categories.map((c) => c.category_id),
      ...Object.keys(grouped),
    ]);
    return Array.from(ids).sort();
  }, [categories, grouped]);

  const labelFor = (cid) => {
    const m = categories.find((c) => c.category_id === cid);
    return m?.label || titleCase(cid);
  };

  const toggleGroup = (cid) =>
    setCollapsed((c) => ({ ...c, [cid]: !c[cid] }));

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-service-catalog-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Layers className="w-5 h-5" /> Service Catalog
            <InfoTooltip
              label="Service Catalog"
              description="Canonical, admin-managed service list. Hosts pick from this when planning a service. Grouped by category. Edit price ranges, durations, and active status."
              source="db.service_catalog"
              where_synced={["Plan a Service step 3", "Host service picker", "Acuity / native booking duration suggestions"]}
            />
          </h1>
          <p className="text-xs text-gray-500">
            Single source of truth for services. Grouped by category, relational select.
          </p>
        </div>
        <div className="relative w-72 max-w-full">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search service name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="sc-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="space-y-3">
          {allCategoryIds.map((cid) => {
            const rows = filteredGroups[cid] || [];
            const isCollapsed = !!collapsed[cid];
            return (
              <Card key={cid} data-testid={`sc-group-${cid}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(cid)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2">
                    {isCollapsed ? (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="font-semibold text-sm">{labelFor(cid)}</span>
                    <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">
                      {rows.length}
                    </Badge>
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      startNew(cid);
                    }}
                    className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 cursor-pointer"
                    data-testid={`sc-add-${cid}`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add service
                  </span>
                </button>

                {!isCollapsed && (
                  <CardContent className="pt-0 space-y-2">
                    {newCategory === cid && (
                      <div className="border border-amber-200 bg-amber-50 rounded p-3">
                        <p className="text-xs font-semibold mb-2">New service in {labelFor(cid)}</p>
                        <ServiceFieldsForm
                          draft={newDraft}
                          setDraft={setNewDraft}
                          categories={categories}
                          testidPrefix="new-svc"
                        />
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" onClick={createNew} data-testid="new-svc-save">
                            <Save className="w-3.5 h-3.5 mr-1" /> Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setNewCategory("");
                              setNewDraft({});
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {rows.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-4">
                        No services in {labelFor(cid)} yet.
                      </p>
                    ) : (
                      rows.map((svc) =>
                        editing === svc.service_catalog_id ? (
                          <div
                            key={svc.service_catalog_id}
                            className="border rounded p-3 bg-gray-50"
                          >
                            <ServiceFieldsForm
                              draft={draft}
                              setDraft={setDraft}
                              categories={categories}
                              testidPrefix={`edit-svc-${svc.service_catalog_id}`}
                            />
                            <div className="flex gap-2 mt-2">
                              <Button size="sm" onClick={saveEdit}>
                                <Save className="w-3.5 h-3.5 mr-1" /> Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={cancelEdit}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div
                            key={svc.service_catalog_id}
                            className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded border-b last:border-b-0"
                            data-testid={`sc-row-${svc.service_catalog_id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {svc.name}
                                {!svc.is_active && (
                                  <Badge className="ml-2 text-[10px] bg-gray-200 text-gray-600 border-0">
                                    inactive
                                  </Badge>
                                )}
                              </p>
                              <p className="text-xs text-gray-500">
                                {svc.duration_min ? `${svc.duration_min} min · ` : ""}
                                {svc.price_low != null
                                  ? `$${svc.price_low}${svc.price_high ? `–$${svc.price_high}` : ""}`
                                  : ""}
                              </p>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => startEdit(svc)}
                              data-testid={`edit-svc-btn-${svc.service_catalog_id}`}
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => deleteService(svc.service_catalog_id)}
                              data-testid={`delete-svc-btn-${svc.service_catalog_id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        )
                      )
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ServiceFieldsForm({ draft, setDraft, categories, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {FIELD_DEFS.map((f) => {
        if (f.type === "category-select") {
          return (
            <div key={f.key}>
              <label className="text-xs text-gray-500 block mb-0.5">{f.label}</label>
              <Select
                value={draft[f.key] || ""}
                onValueChange={(v) => setDraft({ ...draft, [f.key]: v })}
              >
                <SelectTrigger data-testid={`${testidPrefix}-${f.key}`}>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.category_id} value={c.category_id}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }
        if (f.type === "bool") {
          return (
            <div key={f.key} className="flex items-center gap-2 pt-5">
              <input
                type="checkbox"
                id={`${testidPrefix}-${f.key}`}
                checked={!!draft[f.key]}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.checked })}
                data-testid={`${testidPrefix}-${f.key}`}
              />
              <label htmlFor={`${testidPrefix}-${f.key}`} className="text-xs text-gray-600">
                {f.label}
              </label>
            </div>
          );
        }
        return (
          <div key={f.key}>
            <label className="text-xs text-gray-500 block mb-0.5">{f.label}</label>
            <Input
              type={f.type === "number" ? "number" : "text"}
              value={draft[f.key] ?? ""}
              onChange={(e) =>
                setDraft({ ...draft, [f.key]: e.target.value })
              }
              data-testid={`${testidPrefix}-${f.key}`}
            />
          </div>
        );
      })}
    </div>
  );
}
